import React from "react";
import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import { Calendar, Clock, ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { getPostBySlug, blogPosts } from "../data/blogPosts";
import NotFound from "./NotFound";

const BlogPost = () => {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  if (!post) {
    return <NotFound />;
  }

  const url = `https://croissanceconsulting.net/blog/${post.slug}`;

  // Schema.org BlogPosting
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.metaDescription,
    "image": post.image,
    "datePublished": post.dateISO,
    "dateModified": post.dateISO,
    "author": {
      "@type": "Person",
      "name": post.author,
      "url": "https://croissanceconsulting.net/a-propos",
    },
    "publisher": {
      "@type": "Organization",
      "name": "Croissance Consulting",
      "url": "https://croissanceconsulting.net/",
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": url,
    },
  };

  // Related posts (other articles)
  const related = blogPosts.filter((p) => p.slug !== post.slug).slice(0, 2);

  const ArticleContent = post.Content;

  return (
    <>
      <Helmet>
        <title>{post.metaTitle}</title>
        <meta name="description" content={post.metaDescription} />
        <link rel="canonical" href={url} />

        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.metaDescription} />
        <meta property="og:image" content={post.image} />
        <meta property="og:url" content={url} />
        <meta property="article:published_time" content={post.dateISO} />
        <meta property="article:author" content={post.author} />

        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-[#FBF7EF]" data-testid="blog-post-page">
        {/* Back link */}
        <div className="pt-28 pb-2 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-3xl">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-[#6B7A3A] hover:text-[#B65C3A] transition-colors text-sm font-medium"
              data-testid="back-to-blog"
            >
              <ArrowLeft size={16} />
              Retour au blog
            </Link>
          </div>
        </div>

        {/* Header */}
        <header className="pt-6 pb-8 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-3xl">
            <div className="flex flex-wrap items-center gap-4 text-sm text-[#6B7A3A] mb-6">
              <span className="flex items-center gap-2">
                <Calendar size={16} />
                <time dateTime={post.dateISO}>{post.dateFormatted}</time>
              </span>
              <span className="flex items-center gap-2">
                <Clock size={16} />
                {post.readTime} de lecture
              </span>
              <span className="text-[#4A3A2A]/60">·</span>
              <span>Par {post.author}</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#4A3A2A] font-poppins leading-tight mb-6">
              {post.title}
            </h1>
            <p className="text-lg sm:text-xl text-[#2D2D2D] leading-relaxed">
              {post.excerpt}
            </p>
          </div>
        </header>

        {/* Hero image */}
        <div className="px-4 sm:px-6 lg:px-8 mb-12">
          <div className="container mx-auto max-w-4xl">
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img
                src={post.image}
                alt={post.imageAlt}
                className="w-full h-[300px] sm:h-[420px] object-cover"
              />
            </div>
          </div>
        </div>

        {/* Article body */}
        <article className="px-4 sm:px-6 lg:px-8 pb-16">
          <div className="container mx-auto max-w-3xl">
            <div className="article-prose">
              <ArticleContent />
            </div>
          </div>
        </article>

        {/* CTA */}
        <section className="px-4 sm:px-6 lg:px-8 pb-20">
          <div className="container mx-auto max-w-3xl">
            <div
              className="bg-[#4A3A2A] text-white rounded-2xl p-8 sm:p-12 shadow-xl"
              data-testid="blog-cta-block"
            >
              <h2 className="text-2xl sm:text-3xl font-bold font-poppins mb-4">
                {post.cta?.title || "Et si on regardait votre situation ensemble ?"}
              </h2>
              <p className="text-white/90 leading-relaxed mb-6">
                {post.cta?.intro ||
                  "Si cet article vous parle, c'est probablement qu'il y a déjà des leviers concrets à activer dans votre boulangerie ou pâtisserie. Décrivez-moi votre situation en quelques lignes, je vous réponds sous 24 à 48h."}
              </p>
              <Link to={post.cta?.to || "/contact"}>
                <Button
                  className="bg-[#B65C3A] hover:bg-[#9d4e2f] text-white font-medium px-8 py-6 text-lg"
                  data-testid="blog-cta-button"
                >
                  {post.cta?.label || "Proposer un échange"}
                  <ArrowRight size={20} className="ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Related */}
        {related.length > 0 && (
          <section className="px-4 sm:px-6 lg:px-8 pb-24 bg-white py-20">
            <div className="container mx-auto max-w-5xl">
              <h2 className="text-2xl sm:text-3xl font-bold text-[#4A3A2A] font-poppins mb-8 text-center">
                À lire aussi
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {related.map((rp) => (
                  <Link
                    key={rp.slug}
                    to={`/blog/${rp.slug}`}
                    className="bg-[#FBF7EF] rounded-xl p-6 hover:shadow-lg transition-shadow"
                  >
                    <h3 className="text-lg font-bold text-[#4A3A2A] font-poppins mb-2">
                      {rp.title}
                    </h3>
                    <p className="text-sm text-[#2D2D2D] line-clamp-3">
                      {rp.excerpt}
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </div>
    </>
  );
};

export default BlogPost;
